import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { differenceInDays } from 'date-fns';

import { hideLoader, showLoader } from '@topconagriculture/loader';

import { LoadersNames } from '../../ui/ui.constants';
import api from '../../../core/api';
import {
  getRefusals,
  getRefusalsBatches,
  getRefusalsFilterData,
  getRefusalsPreset,
  updateRefusalsDelivery,
} from './refusals.actions';
import { notificationError } from '../../../core/helpers/utils/notificationUtils';
import { composeErrorMessage } from '../../../core/helpers/utils/utils';
import { toLocalDate, toTimezoneDate } from '../../../core/helpers/utils/dateUtils';
import { settingsSelector } from '../../selectors';
import { compose403ErrorMessage } from '../../../core/helpers/utils/sagaUtils';

function* getRefusalsBatchesSaga({ payload }: ReturnType<typeof getRefusalsBatches.started>) {
  yield put(showLoader(LoadersNames.fetchRefusalsBatches));
  try {
    const { startDate, endDate, groupIds, groupTypeIds } = payload;
    const timezone = yield select(settingsSelector.getTimeZone);
    const daysBack = differenceInDays(endDate, startDate);
    const batchesList = yield call(
      api.dataTools.getLoadsDeliveriesBatches,
      daysBack,
      toTimezoneDate(toLocalDate(endDate), timezone),
      true,
      'np',
      groupIds,
      groupTypeIds,
    );
    const result = batchesList.filter((x) => x.numberOfDeliveries > 0);
    yield put(
      getRefusalsBatches.done({
        params: payload,
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchRefusalsBatches));
}

function* updateRefusalsDeliverySaga(action: ReturnType<typeof updateRefusalsDelivery.started>) {
  yield put(showLoader(LoadersNames.updateRefusalsDelivery));
  try {
    const { data, formName } = action.payload;
    const { batchId, deliveryId, refusalWeight } = data;
    const result = yield call(api.dataTools.updateDelivery, batchId, deliveryId, { refusalWeight }, false, formName);
    yield put(
      updateRefusalsDelivery.done({
        params: action.payload,
        result,
      }),
    );
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.updateRefusalsDelivery));
}

function* getRefusalsSaga({ payload }: ReturnType<typeof getRefusals.started>) {
  yield put(showLoader(LoadersNames.fetchRefusals));
  try {
    const { startDate, endDate, groupIds, groupTypeIds } = payload;
    const timezone = yield select(settingsSelector.getTimeZone);

    const daysBack = differenceInDays(endDate, startDate);
    const result = yield call(
      api.refusals.getRefusals,
      daysBack,
      toTimezoneDate(toLocalDate(endDate), timezone),
      groupIds,
      groupTypeIds,
    );
    yield put(
      getRefusals.done({
        params: payload,
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchRefusals));
}

export function* getRefusalsFilterDataSaga() {
  yield put(showLoader(LoadersNames.fetchRefusalsSettings));
  try {
    const result = yield call(api.refusals.getReportFilterData);
    yield put(
      getRefusalsFilterData.done({
        params: {},
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  } finally {
    yield put(hideLoader(LoadersNames.fetchRefusalsSettings));
  }
}

export function* getRefusalsPresetSaga() {
  yield put(showLoader(LoadersNames.fetchRefusals));
  try {
    const result = yield call(api.refusals.getPresets);
    yield put(
      getRefusalsPreset.done({
        params: {},
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchRefusals));
}

export function* watchRefusalsSaga() {
  yield all([
    takeLatest(getRefusalsBatches.started, getRefusalsBatchesSaga),
    takeLatest(updateRefusalsDelivery.started, updateRefusalsDeliverySaga),
    takeLatest(getRefusals.started, getRefusalsSaga),
    takeLatest(getRefusalsFilterData.started, getRefusalsFilterDataSaga),
    takeLatest(getRefusalsPreset.started, getRefusalsPresetSaga),
  ]);
}
